// The searchable dropdown this page picks with: the parent of a position, its
// classifier, and the parent filter on the bar.
//
// SHAPE — the wireframe's `Select searchable` (components/Select.tsx), chosen by
// the owner on 2026-09-10 over the shape this file had before (a text field typed
// into directly). A trigger that reads as a select, and a panel carrying its own
// search box:
//
//   [ Chișinău                              ⌄ ]
//   ┌──────────────────────────────────────────┐
//   │ [🔍 Caută…]                     (sticky) │
//   │ Chișinău          municipiu · Rep. Mold. │
//   │ …                                        │
//   │ Încă 1 664. Scrie ca să restrângi lista. │
//   └──────────────────────────────────────────┘
//
// MUD does not draw this. Its only word on the subject is one line of guidance —
// "Select box · to select a single item with type-ahead functionality from a
// longer list" (`mud/02-layouts-patterns.md:208`) — and then neither the Select
// page nor the Menu page puts a search field anywhere in the anatomy. So the
// wireframe is the authority (AGENTS.md R6) and MUD supplies the parts: the
// trigger is `mud-text-input` read-only wearing `mud-select`'s chevron, the
// search box is `mud-search-input` — the component MUD reserves for
// exactly this kind of in-place filtering — and the panel is styled from
// `--select-listbox-*` / `--select-option-*`.
//
// WHY NOT `mud-select` — it takes its options as static `<option>` children and a button
// trigger, so it cannot carry a query. It also cannot open inside `mud-modal`:
// its listbox is `position: absolute` and the dialog clips it twice, at
// `.body { overflow-y: auto }` and `.surface { overflow: hidden }`, both in AGE's
// shadow root and neither reachable from a consumer stylesheet.
// AGE-BUG(select-listbox-clipped-in-modal) · vendor/age-design/SOURCE_COMMIT 7322a157
// per docs/ui-shape-geography.md §The pickers
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ElementRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { MudIcon, MudTextInput, MudSearchInput } from '@egov-moldova/mud-react';
import { useAnchoredOverlay } from '@simsm/ui-shared';
import { claimDropdown, releaseDropdown } from './dropdown-coordinator';

export interface PickerOption {
  id: string;
  name: string;
  /** "raion · Republica Moldova" — the wireframe's `sub` line. Empty draws none. */
  detail: string;
  /** Matched on as well as the name, so a code typed out of the table finds its position. */
  searchText: string;
}

interface GeographyPickerProps {
  options: readonly PickerOption[];
  value: string;
  invalid: boolean;
  /** Drawn by the control. Omit where the surface has already drawn one. */
  label?: string | undefined;
  /** Names the control where the surface drew the label itself. */
  ariaLabel?: string | undefined;
  required?: boolean | undefined;
  /** What the trigger reads when nothing is chosen. */
  placeholder: string;
  /**
   * Whether the panel carries a search box. The wireframe decides this per
   * field rather than per component (`Select`'s own `searchable` prop): the two
   * parent pickers take it, because the nomenclator is ~1 700 positions;
   * `Tip` and `Statut` do not, because they hold a handful each and a search
   * field over six options is a control asking to be used for nothing
   * (AdminLocalitati.tsx:129 and :180 against :132 and :178).
   *
   * Defaults to true: every caller that existed before this prop was a parent
   * picker, so the default is the behaviour they already had.
   */
  searchable?: boolean | undefined;
  searchPlaceholder: string;
  emptyText: string;
  moreText: (count: number) => string;
  errorText?: string | undefined;
  helperText?: string | undefined;
  onPick: (id: string) => void;
}

// The wireframe's own ceiling: the nomenclator is ~1 700 positions, and a panel
// that renders all of them is a scrollbar rather than a choice. Fifty, then a row
// saying how many are left and what to do about it (`Select.tsx`, `PRAG`).
const SHOWN_LIMIT = 50;

function normalize(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export function GeographyPicker({
  options,
  value,
  invalid,
  label,
  ariaLabel,
  required,
  placeholder,
  searchable = true,
  searchPlaceholder,
  emptyText,
  moreText,
  errorText,
  helperText,
  onPick,
}: GeographyPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<ElementRef<typeof MudTextInput>>(null);
  const searchRef = useRef<ElementRef<typeof MudSearchInput>>(null);
  const listboxId = `geo-picker-${useId()}`;

  const selected = options.find((option) => option.id === value);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setQuery('');
    if (refocus) triggerRef.current?.focus();
  }, []);

  // A stable identity, so the coordinator can tell this picker's close from
  // another's.
  const closeForRegistry = useCallback(() => close(false), [close]);
  useEffect(() => () => releaseDropdown(closeForRegistry), [closeForRegistry]);

  const openPanel = useCallback(() => {
    claimDropdown(closeForRegistry);
    setQuery('');
    setActive(
      Math.max(
        0,
        options.findIndex((option) => option.id === value),
      ),
    );
    setOpen(true);
  }, [closeForRegistry, options, value]);

  useEffect(() => {
    if (!open) {
      releaseDropdown(closeForRegistry);
      return undefined;
    }
    function onPointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close, closeForRegistry]);

  // The search box takes focus when the panel opens, which is what makes typing
  // the first thing the reader can do (`Select.tsx`'s `autoFocus`) — and what
  // takes the caret out of the trigger, which is a select and has no business
  // holding one.
  //
  // The NATIVE input, not the host: `mud-search-input` declares no
  // `delegatesFocus` and exposes no `setFocus`, so `host.focus()` moves nothing
  // and focus stayed on <body>. Measured before the fix: `document.activeElement`
  // was BODY with the panel open.
  useEffect(() => {
    if (!open || !searchable) return;
    const host = searchRef.current as
      | (HTMLElement & { componentOnReady?: () => Promise<unknown> })
      | null;
    if (!host) return;
    const focusNative = (): void => host.shadowRoot?.querySelector('input')?.focus();
    // After the frame, not during it. Opening by pointer runs mousedown (which
    // focuses the trigger), then click, then this effect — but the focus the
    // pointer set lands last on the element it belongs to, and it took the caret
    // back. Measured: with a plain call the trigger held focus and typing reached
    // nothing, because the query lives in the box below.
    let frame = 0;
    const ready = host.componentOnReady?.();
    const run = (): void => {
      frame = requestAnimationFrame(focusNative);
    };
    if (ready && typeof ready.then === 'function') void ready.then(run);
    else run();
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const matches = useMemo(() => {
    const needle = normalize(query.trim());
    if (needle.length === 0) return options;
    return options.filter((option) => normalize(option.searchText).includes(needle));
  }, [options, query]);
  const shown = matches.length > SHOWN_LIMIT ? matches.slice(0, SHOWN_LIMIT) : matches;

  useEffect(() => {
    if (active >= shown.length) setActive(0);
  }, [shown.length, active]);

  function choose(option: PickerOption): void {
    onPick(option.id);
    close(true);
  }

  function move(direction: 1 | -1): void {
    if (shown.length === 0) return;
    setActive((current) => (current + direction + shown.length) % shown.length);
  }

  // ONE handler, on the wrapper rather than on the two AGE fields: `keydown` is a
  // composed event, so it crosses both shadow boundaries and arrives here
  // whichever of them is focused. `MedicineCombobox` catches its keys the same
  // way, and for the same reason — the React wrappers type their handler against
  // the Stencil element, which is not assignable to `Element` under
  // `exactOptionalPropertyTypes`.
  //
  // Where the key came from is the only difference: a space typed into the search
  // box is part of the query, not a choice.
  function onKeyDown(event: ReactKeyboardEvent): void {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const fromTrigger = !panelRef.current?.contains(event.target as Node);
    const commit = (): void => {
      const option = shown[active];
      if (option) choose(option);
    };
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (open) move(1);
        else openPanel();
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (open) move(-1);
        else openPanel();
        return;
      case 'Home':
        if (!open) return;
        event.preventDefault();
        setActive(0);
        return;
      case 'End':
        if (!open) return;
        event.preventDefault();
        setActive(Math.max(0, shown.length - 1));
        return;
      case 'Enter':
        event.preventDefault();
        if (open) commit();
        else openPanel();
        return;
      case ' ':
        if (!fromTrigger) return;
        event.preventDefault();
        if (open) commit();
        else openPanel();
        return;
      case 'Escape':
        if (!open) return;
        event.preventDefault();
        // An Escape meant for this panel is not one meant for a modal the panel
        // happens to be standing in.
        event.stopPropagation();
        close(true);
        return;
      case 'Tab':
        if (open) close(false);
        return;
      default:
        // Type-ahead, which is the whole reason MUD calls this control a select
        // box "with type-ahead functionality". A character typed while the
        // trigger holds focus belongs to the query, so it is routed into the
        // search box rather than swallowed by a read-only field — the panel
        // opens if it is not open yet, exactly as it would on ArrowDown.
        //
        // It also makes the behaviour independent of where the pointer left
        // focus: opening by click leaves focus on the trigger (the browser sets
        // it at mousedown, after which nothing this component does reliably wins
        // it back), and without this the first keystrokes would land nowhere.
        if (event.key.length !== 1) return;
        event.preventDefault();
        if (!open) openPanel();
        // With no search box there is nowhere to route the character, so the
        // keystroke jumps to the first option starting with it and focus stays
        // on the trigger — which is what a plain select does, and what MUD means
        // by "type-ahead" for one.
        if (!searchable) {
          const needle = normalize(event.key);
          const hit = options.findIndex((option) => normalize(option.name).startsWith(needle));
          if (hit >= 0) setActive(hit);
          return;
        }
        setQuery((current) => current + event.key);
        setActive(0);
        searchRef.current?.shadowRoot?.querySelector('input')?.focus();
    }
  }

  const { style: panelStyle } = useAnchoredOverlay({
    open,
    triggerRef: rootRef,
    overlayRef: panelRef,
    matchTriggerWidth: true,
  });

  return (
    <div
      ref={rootRef}
      className="simsm-geo-picker"
      data-open={open ? 'true' : undefined}
      onKeyDown={onKeyDown}
    >
      {/* Read-only, as the wireframe's trigger is: the value is chosen, not typed.
          MUD says the same of its select box — `cursor-system-arrow`, "a select is
          chosen, not typed into" (mud/components/select.md). */}
      <MudTextInput
        ref={triggerRef}
        size="md"
        readonly
        value={selected ? selected.name : ''}
        placeholder={placeholder}
        invalid={invalid}
        {...(label ? { label } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        {...(required ? { required: true } : {})}
        {...(errorText ? { errorText } : helperText ? { helperText } : {})}
        onClick={() => (open ? close(false) : openPanel())}
      >
        <span
          slot="icon-end"
          className="simsm-geo-picker__chevron"
          data-open={open ? 'true' : undefined}
          aria-hidden="true"
        >
          <MudIcon name="chevron-bottom" size={20} />
        </span>
      </MudTextInput>

      {/* NOT portalled to <body>. Two of the three call sites open inside
          `mud-modal`, which shows a native <dialog> with showModal() — that dialog
          renders in the browser's TOP LAYER, so anything under <body> paints
          beneath it however high its z-index (measured at z-index 80, still
          behind). Left in the dialog's own subtree the panel is in the top layer
          with it, and `useAnchoredOverlay`'s `position: fixed` keeps the modal
          body's overflow from clipping it. */}
      {open ? (
        <div ref={panelRef} className="simsm-geo-picker__panel" style={panelStyle}>
          {/* Sticky, so it stays reachable while the list scrolls under it —
              `Select.tsx`'s own `position: sticky; top: 0`. */}
          {searchable ? (
            <div className="simsm-geo-picker__search">
              <MudSearchInput
                shape="rectangular"
                ref={searchRef}
                size="md"
                value={query}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onMudInput={(e: CustomEvent<{ value: string }>) => {
                  setQuery(e.detail.value);
                  setActive(0);
                }}
                onMudClear={() => {
                  setQuery('');
                  setActive(0);
                }}
              />
            </div>
          ) : null}

          {shown.length === 0 ? (
            <p className="simsm-geo-picker__empty" role="status">
              {emptyText}
            </p>
          ) : (
            <div
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel ?? label ?? placeholder}
              className="simsm-geo-picker__list"
            >
              {shown.map((option, index) => (
                <div
                  key={option.id}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={option.id === value}
                  className="simsm-geo-picker__option"
                  data-selected={option.id === value ? 'true' : undefined}
                  data-active={index === active ? 'true' : undefined}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(option)}
                >
                  <span className="simsm-geo-picker__option-text">
                    <span className="simsm-geo-picker__option-name">{option.name}</span>
                    {option.detail ? (
                      <span className="simsm-geo-picker__option-detail">{option.detail}</span>
                    ) : null}
                  </span>
                  {/* Always slotted, hidden when it means nothing: the rows keep one
                      width so the chosen one does not reflow, which is how AGE's own
                      option draws its check. */}
                  <span className="simsm-geo-picker__option-check" aria-hidden="true">
                    <MudIcon name="checkmark-small" size={20} />
                  </span>
                </div>
              ))}
            </div>
          )}

          {matches.length > SHOWN_LIMIT ? (
            <p className="simsm-geo-picker__more" role="status">
              {moreText(matches.length - SHOWN_LIMIT)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
